import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from '../lib/apiUrl';

const EntitlementsContext = createContext(null);

// Mapa recurso → claves de uso/límite en la respuesta de /api/entitlements
const RESOURCE_MAP = {
  teams: { usage: 'teams', limit: 'teams_max', label: 'equipos' },
  sessions: { usage: 'sessions', limit: 'sessions_saved_max', label: 'sesiones' },
  matches: { usage: 'matches_this_month', limit: 'matches_per_month_max', label: 'partidos' },
  match_calls: { usage: 'match_calls_this_month', limit: 'match_calls_per_month_max', label: 'convocatorias' },
};

export function EntitlementsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setData(null); setLoading(false); return; }
    try {
      const r = await axios.get(`${API_URL}/api/entitlements`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // ¿Puede crear más de este recurso? Si aún no hay datos, no bloqueamos.
  const canCreate = useCallback((resource) => {
    const m = RESOURCE_MAP[resource];
    if (!m || !data) return true;
    const used = data.usage?.[m.usage] ?? 0;
    const max = data.limits?.[m.limit] ?? Infinity;
    return used < max;
  }, [data]);

  // Texto para el candado cuando se agota el cupo
  const limitReason = useCallback((resource) => {
    const m = RESOURCE_MAP[resource];
    const max = m && data ? data.limits?.[m.limit] : null;
    if (max == null) return 'Has alcanzado el límite de tu plan. Mejora a Pro para continuar.';
    return `Límite del plan free alcanzado (${max} ${m.label}). Mejora a Pro para uso ilimitado.`;
  }, [data]);

  const value = {
    loading,
    plan: data?.plan || user?.selected_plan || 'free',
    adminAccess: !!data?.admin_access,
    newSessionPath: (data?.admin_access || ['pro', 'club', 'enterprise'].includes(data?.plan || user?.selected_plan)) ? '/sesiones/elite' : '/sesiones/nueva',
    limits: data?.limits || {},
    usage: data?.usage || {},
    features: data?.features || {},
    trial: data?.trial || null,
    canCreate,
    limitReason,
    refresh,
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export function useEntitlements() {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    // Fallback seguro fuera del provider (no bloquea)
    return {
      loading: false, plan: 'free', limits: {}, usage: {}, features: {}, trial: null,
      newSessionPath: '/sesiones/nueva',
      canCreate: () => true, limitReason: () => '', refresh: () => {},
    };
  }
  return ctx;
}
